import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon, {
  compareKeyMetricsCreatorTips,
  compareKeyMetricsEngagementRateTips,
  compareKeyMetricsEngagementTips,
  compareKeyMetricsOfficeAccountEngagementTips,
  compareKeyMetricsOfficeAccountViewsTips,
  compareKeyMetricsPostCreatorTips,
  compareKeyMetricsPostImpressionsTips,
  compareKeyMetricsPostPublicationsTips,
  compareKeyMetricsPostsEngagementTips,
  compareKeyMetricsPostViewsTips,
  compareKeyMetricsPublicationsTips,
  compareKeyMetricsVideoCreatorTips,
  compareKeyMetricsVideoEngagementTips,
  compareKeyMetricsVideoImpressionsTips,
  compareKeyMetricsVideoPublicationsTips,
  compareKeyMetricsVideoViewsTips,
  compareKeyMetricsViewsTips,
  shortGoogleText,
  TipsMapType,
  compareKeyMetricsNegativeTips,
  compareKeyMetricsPositiveTips,
  compareKeyMetricsImpressionsTips,
  communitySteamingCommonTitleText,
} from '@op/components/BubbleCommon';
import ChannelLabel from '@op/components/ChannelLabel';
import {
  brandHealthRenderHelper,
  sentimentNumber,
  tryTransNumber,
} from '@op/components/DownloadCommon/download-page/render-helper';
import HoverSvgIcon from '@op/components/HoverSvgIcon';
import { googleTrendsRenderHelper } from '@op/pages/download/renderHelper';
import { CommonColumnsType } from '@op/types/table';
import { mathFormat } from '@op/utils';
import {
  brandHealthFormat,
  percentageFormat,
  reviewsFormat,
  scoreFormat,
  sentimentFormat,
  specialSecimalFormatPercentage,
} from '@op/utils/value';
import React from 'react';
/** ****************Sentiment Mentions******************/

export type MentionsSentimentTabType =
  | 'mentions'
  | 'brand_health'
  | 'sentiment';

export type MentionsSentimentSubSortType =
  | 'by_channel'
  | 'by_language'
  | 'by_sentiment'
  | 'by_mentions';

const sortByOptions: Array<{
  text: string;
  value: MentionsSentimentSubSortType;
}> = [
  { text: 'By Channel', value: 'by_channel' },
  { text: 'By Language', value: 'by_language' },
];

export const mentionsSentimentTab: Array<{
  text: string;
  value: MentionsSentimentTabType;
  options: Array<{ text: string; value: MentionsSentimentSubSortType }>;
  formatData: Function;
}> = [
  {
    text: 'Mentions',
    value: 'mentions',
    options: [
      {
        text: 'By Mentions Type',
        value: 'by_mentions',
      },
      { text: 'By Sentiment', value: 'by_sentiment' },
      ...sortByOptions,
    ],
    formatData: mathFormat,
  },
  {
    text: 'Brand Health',
    value: 'brand_health',
    options: sortByOptions,
    formatData: brandHealthFormat,
  },
  {
    text: 'Sentiment',
    value: 'sentiment',
    options: sortByOptions,
    formatData: mathFormat,
  },
];

export type TableColumnsFuncType = (
  list: Array<{ header: string; key: string; format?: (value: any) => string }>,
) => Array<CommonColumnsType>;
const channelLanguageTable: TableColumnsFuncType = (list) => {
  return [
    ...list.map(({ header, key, format = reviewsFormat, ...item }) => {
      return {
        header: header,
        key: key,
        format,
        ...item,
      };
    }),
  ];
};

/** ****************Sentiment Mentions******************/

/** ****************Game Store******************/
export type GameStoreOneOptionsType = 'app store' | 'google play';
export const mobileGameStoreLabel = {
  'app store': 'App Store',
  'google play': 'Google Play',
};
export const gameStoreOneOptions: (type: GameStoreOneOptionsType) => Array<{
  text: string | any;
  value: GameStoreOneOptionsType;
}> = (type) => [
  {
    text: (
      <div className="dbo-inlineflex">
        <ChannelLabel
          channel_name={'google play'}
          showText={false}
        ></ChannelLabel>
        <span>Google Play</span>
        <BubbleCommon
          placement="top"
          tipsMap={shortGoogleText}
          customIcon={
            <HoverSvgIcon selected={type === 'google play'}></HoverSvgIcon>
          }
        ></BubbleCommon>
      </div>
    ),
    value: 'google play',
  },
  {
    text: (
      <div className="dbo-inlineflex">
        <ChannelLabel
          channel_name={'app store'}
          showText={false}
        ></ChannelLabel>
        <span>App Store</span>
      </div>
    ),
    value: 'app store',
  },
];
export type GameStoreTwoOptionsType = 'lifetime' | 'selected';
export const gameStoreTwoOptions: Array<{
  text: string;
  value: GameStoreTwoOptionsType;
}> = [
  { text: 'Lifetime Rating', value: 'lifetime' },
  { text: 'Added Reviews', value: 'selected' },
];

export const commonGameStore: Array<CommonColumnsType> = [
  {
    header: 'Positive%',
    key: 'positive',
    format: (value) => percentageFormat(value * 100),
    numFmt: '0.00%',
    tips: compareKeyMetricsPositiveTips,
    dataType: 'percent',
  },
  {
    header: 'Negative%',
    key: 'negative',
    format: (value) => percentageFormat(value * 100),
    numFmt: '0.00%',
    tips: compareKeyMetricsNegativeTips,
    dataType: 'percent',
  },
];

const gameStoreLifetime: Array<CommonColumnsType> = [
  {
    header: 'Total Rating',
    key: 'mentions',
    progress: true,
    select: 'lifetime',
  },
  {
    header: 'Lifetime Rating',
    key: 'score',
    format: (value) => scoreFormat(value, 1),
    numFmt: ['0.0'],
    select: 'lifetime_rating',
  },
  ...commonGameStore,
];
const gameStoreAdded: Array<CommonColumnsType> = [
  {
    header: 'Total Reviews',
    key: 'mentions',
    progress: true,
    select: 'selected',
  },
  {
    header: 'Average Rating',
    key: 'score',
    numFmt: ['0.0'],
    format: (value) => scoreFormat(value, 1),
    select: 'select_avg_rating',
  },
  ...commonGameStore,
];

/** ****************Game Store******************/

/** ****************Steam******************/

export type SteamOptionsType =
  | 'all_reviews'
  | 'recent_reviews'
  | 'added_reviews';
export const steamOptions: Array<{
  text: string;
  value: SteamOptionsType;
}> = [
  { text: 'All Reviews', value: 'all_reviews' },
  {
    text: 'Recent Reviews',
    value: 'recent_reviews',
  },
  {
    text: 'Added Reviews',
    value: 'added_reviews',
  },
];

const commonSteam: (key: string) => Array<CommonColumnsType> = (key) => [
  {
    header: 'Positive%',
    key: `${key}_recommend`,
    format: (value) => percentageFormat(value * 100, '-', 0),
    numFmt: '0%',
    tips: compareKeyMetricsPositiveTips,
    select: 'positive',
    dataType: 'percent',
  },
  {
    header: 'Negative%',
    key: `${key}_non_recommend`,
    format: (value) => percentageFormat(value * 100, '-', 0),
    tips: compareKeyMetricsNegativeTips,
    numFmt: '0%',
    select: 'negative',
    dataType: 'percent',
  },
];

const steamColumns: (
  header: string,
  key: string,
) => Array<CommonColumnsType> = (header, key) => [
  {
    header: header,
    key: key,
    format: (value) => reviewsFormat(value),
    progress: true,
  },
  ...commonSteam(key),
];
/** ****************Steam******************/

/** ****************Community******************/
export type CommunityOptionsType =
  | 'total'
  | 'videos'
  | 'posts'
  | 'streaming'
  | 'official_accounts'
  | 'official_account_followers';

export const getCommunityOptions: (
  formatMessage: any,
  tab?: CommunityOptionsType,
) => {
  text: string | any;
  value: CommunityOptionsType;
}[] = (formatMessage, tab) => [
  {
    text: (
      <div className="dbo-inlineflex">
        <SvgIcon type="studio-all"></SvgIcon>
        <span>{formatMessage({ id: 'Total' })}</span>
      </div>
    ),
    value: 'total',
  },
  {
    text: (
      <div className="dbo-inlineflex">
        <SvgIcon type="studio-video"></SvgIcon>
        <span>{formatMessage({ id: 'Video' })}</span>
      </div>
    ),
    value: 'videos',
  },
  {
    text: (
      <div className="dbo-inlineflex">
        <SvgIcon type="studio-posts"></SvgIcon>
        <span>{formatMessage({ id: 'Posts' })}</span>
      </div>
    ),
    value: 'posts',
  },
  {
    text: (
      <div className="dbo-inlineflex">
        <SvgIcon type="studio-streaming"></SvgIcon>
        <span>{formatMessage({ id: 'Streaming' })}</span>
        <BubbleCommon
          placement="top"
          selected={tab === 'streaming'}
          tipsMap={communitySteamingCommonTitleText}
        ></BubbleCommon>
      </div>
    ),
    value: 'streaming',
  },
  {
    text: (
      <div className="dbo-inlineflex">
        <SvgIcon type="offical-accounts"></SvgIcon>
        <span>{formatMessage({ id: 'Official Accounts' })}</span>
      </div>
    ),
    value: 'official_accounts',
  },
];

const impressionsColumns: (tips?: TipsMapType) => CommonColumnsType = (
  tips = compareKeyMetricsImpressionsTips,
) => ({
  header: 'Potential Impressions',
  key: 'impressions',
  progress: true,
  tips,
});

const publicationsColumns: (tips?: TipsMapType) => CommonColumnsType = (
  tips = compareKeyMetricsPublicationsTips,
) => ({
  header: 'Publications',
  key: 'publications',
  progress: true,
  tips,
});

const creatorsColumns: (tips?: TipsMapType) => CommonColumnsType = (
  tips = compareKeyMetricsCreatorTips,
) => ({
  header: 'Number of Creators',
  key: 'creators',
  progress: true,
  tips,
});
const viewsColumns: (
  tips?: TipsMapType,
  headerText?: string,
) => CommonColumnsType = (tips, headerText = 'Views') => ({
  header: headerText,
  key: 'views',
  progress: true,
  tips,
});
const engagementColumns: (tips?: TipsMapType) => CommonColumnsType = (
  tips = compareKeyMetricsEngagementTips,
) => ({
  header: 'Engagement',
  key: 'engagement',
  progress: true,
  tips: tips,
});
const hoursWatchedColumns: CommonColumnsType = {
  header: 'Hours Watched',
  key: 'hoursWatched',
  progress: true,
};
const sessionsColumns: CommonColumnsType = {
  header: 'Sessions',
  key: 'publications',
  progress: true,
};
const streamersColumns: CommonColumnsType = {
  header: 'Streamers',
  key: 'creators',
  progress: true,
};

const peakCCVColumns: CommonColumnsType = {
  header: 'Peak CCV',
  key: 'peakCCV',
  progress: true,
};
const avgCCVColumns: CommonColumnsType = {
  header: 'Avg CCV',
  key: 'avgCCV',
  progress: true,
};
const followerColumns: CommonColumnsType = {
  header: 'Followers',
  key: 'official_account_followers',
  progress: true,
};
const engagementRateColumns: CommonColumnsType = {
  header: 'Engagement Rate',
  key: 'engagement_rate',
  progress: true,
  format: (value: number) => specialSecimalFormatPercentage(value),
  numFmt: '0.00%',
  tips: compareKeyMetricsEngagementRateTips,
};

/** ****************Community******************/
export type CompareKeyMetricsTableOneKey =
  | 'MentionsSentiment'
  | 'GameStore'
  | 'Steam'
  | 'Community'
  | 'PR';

export const compareKeyMetricsTable: {
  [key in CompareKeyMetricsTableOneKey]:
    | {
        [key in
          | MentionsSentimentTabType
          | GameStoreOneOptionsType
          | SteamOptionsType
          | CommunityOptionsType]?:
          | {
              [key in MentionsSentimentSubSortType | GameStoreTwoOptionsType]?:
                | CommonColumnsType[]
                | TableColumnsFuncType;
            }
          | CommonColumnsType[];
      }
    | CommonColumnsType[];
} = {
  MentionsSentiment: {
    mentions: {
      by_mentions: [
        {
          header: 'Total Mentions',
          key: 'overall',
          progress: true,
        },
        {
          header: 'Game Store Reviews',
          key: 'game_store_reviews',
        },
        {
          header: 'Videos',
          key: 'videos',
        },
        {
          header: 'Posts-2',
          key: 'account_posts',
        },
        {
          header: 'Comments-1',
          key: 'exclude_discord_comments',
        },
        {
          header: 'Discord Chats',
          key: 'discord_mentions',
        },
      ],
      by_sentiment: [
        {
          header: 'Total Mentions',
          key: 'overall',
          progress: true,
        },
        {
          header: 'Positive',
          key: 'positive_mentions',
        },
        {
          header: 'Neutral',
          key: 'neutral_mentions',
        },
        {
          header: 'Negative',
          key: 'negative_mentions',
        },
      ],
      by_channel: channelLanguageTable,
      by_language: channelLanguageTable,
    },
    brand_health: {
      by_channel: channelLanguageTable,
      by_language: channelLanguageTable,
    },
    sentiment: {
      by_channel: channelLanguageTable,
      by_language: channelLanguageTable,
    },
  },
  GameStore: {
    'google play': {
      lifetime: gameStoreLifetime,
      selected: gameStoreAdded,
    },
    'app store': {
      lifetime: gameStoreLifetime,
      selected: gameStoreAdded,
    },
  },
  Steam: {
    all_reviews: steamColumns('All Reviews', 'all_reviews'),
    recent_reviews: steamColumns('Recent Reviews', 'recent_reviews'),
    added_reviews: steamColumns('Added Reviews', 'added_reviews'),
  },
  Community: {
    total: [
      impressionsColumns(),
      publicationsColumns(),
      creatorsColumns(),
      viewsColumns(compareKeyMetricsViewsTips),
      engagementColumns(),
      engagementRateColumns,
      // hoursWatchedColumns,
    ],
    videos: [
      impressionsColumns(compareKeyMetricsVideoImpressionsTips),
      publicationsColumns(compareKeyMetricsVideoPublicationsTips),
      creatorsColumns(compareKeyMetricsVideoCreatorTips),
      viewsColumns(compareKeyMetricsVideoViewsTips, 'Views-1'),
      engagementColumns(compareKeyMetricsVideoEngagementTips),
      engagementRateColumns,
    ],
    posts: [
      impressionsColumns(compareKeyMetricsPostImpressionsTips),
      publicationsColumns(compareKeyMetricsPostPublicationsTips),
      creatorsColumns(compareKeyMetricsPostCreatorTips),
      viewsColumns(compareKeyMetricsPostViewsTips),
      engagementColumns(compareKeyMetricsPostsEngagementTips),
      engagementRateColumns,
    ],
    streaming: [
      // sessionsColumns,
      // streamersColumns, // TODO: 数据不全暂时隐藏
      hoursWatchedColumns,
      peakCCVColumns,
      avgCCVColumns,
    ],
    official_accounts: [
      impressionsColumns(),
      publicationsColumns(),
      viewsColumns(compareKeyMetricsOfficeAccountViewsTips),
      engagementColumns(compareKeyMetricsOfficeAccountEngagementTips),
      engagementRateColumns,
      // followerColumns, // TODO: 数据不全暂时隐藏
    ],
  },
  PR: [
    {
      header: 'All Articles',
      key: 'all_articles',
    },
    {
      header: 'Positive Articles',
      key: 'positive_articles',
    },
    {
      header: 'Negative Articles',
      key: 'negative_articles',
    },
    {
      header: 'Neutral Articles',
      key: 'neutral_articles',
    },
  ],
};

export const getCompareKeyMetricsNavigatorOptions = (
  entity_type: any,
  formatMessage: any,
) => {
  const isMobile = entity_type === 'mobile';
  const isConsole = entity_type === 'console';
  let list = [
    {
      text: formatMessage({ id: 'Mentions & Sentiment' }),
      value: 'Mentions & Sentiment',
    },
    {
      text: formatMessage({ id: isMobile ? 'Game Store' : 'Steam' }),
      value: isMobile ? 'Game Store' : 'Steam',
    },
    {
      text: formatMessage({ id: 'Community' }),
      value: 'Community',
    },
    {
      text: 'PR',
      value: 'PR',
    },
    {
      text: formatMessage({ id: 'Google Trends' }),
      value: 'Google Trends',
    },
  ];

  // 因为console 不是 mobile，所以这里过滤掉steam
  if (isConsole) {
    list = list.filter((item) => item.value !== 'Steam');
  }

  return list;
};

export const mentionsSentimentFormatFuncMap: any = {
  sentiment: sentimentFormat,
  brand_health: brandHealthFormat,
  mentions: reviewsFormat,
};

export const mentionsSentimentRenderHelperFuncMap: any = {
  sentiment: {
    renderHelper: [sentimentNumber],
    numFmt: ['0.00'],
  },
  brand_health: {
    renderHelper: [tryTransNumber, brandHealthRenderHelper],
    numFmt: ['0'],
  },
  mentions: {},
  lifetime: {
    numFmt: ['0.0'],
  },
  google_trends: {
    renderHelper: [googleTrendsRenderHelper],
    numFmt: ['0'],
  },
  engagement_rate: {
    numFmt: ['0.00%'],
  },
  lifetime_rating: {
    numFmt: ['0.0'],
  },
  lifetime_positive: { numFmt: ['0.00%'] },
  lifetime_negative: { numFmt: ['0.00%'] },
  select_avg_rating: { numFmt: ['0.0'] },
  select_positive_mentions: { numFmt: ['0.00%'] },
  select_negative_mentions: { numFmt: ['0.00%'] },
  all_reviews_positive: { numFmt: ['0.00%'] },
  all_reviews_negative: { numFmt: ['0.00%'] },
  recent_reviews_positive: { numFmt: ['0.00%'] },
  recent_reviews_negative: { numFmt: ['0.00%'] },
  added_reviews_positive: { numFmt: ['0.00%'] },
  added_reviews_negative: { numFmt: ['0.00%'] },
};

export enum TableHeight {
  BELOW = 309,
  CHART_TABLE = 285.5,
  CHART = 299,
  CHART_LOADING = 337,
  CHART_WITHOUT_SUBTITLE = 337,
}

export const OverallColumnLabel: { [key: string]: string } = {
  mentions: 'Total Mentions',
  brand_health: 'Overall Brand Health',
  sentiment: 'Overall Sentiment',
};
